import React, { useEffect, useState, useMemo } from "react";
import { Card, Typography, Tabs, Table, Select, Button, Space, Tag, Modal, message, Spin } from "antd";
import { MinusCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Icon } from "Components";
import { ColumnsType } from "antd/es/table";
import { MappingItem, PropertyGroups, TenantMapping } from "../Mapping";
import { debounce } from "lodash";
import { getExternalMerchants, PciPortalProperty } from "Services/Api/PciCompliance/Mapping.Service";
import { useTranslation } from "react-i18next";

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const DEBOUNCE_TIME = 400;

interface MappingWizardStep3Props {
  tenantInfo: TenantMapping;
  fileName: string;
  importedData: MappingItem[];
  onNext: (mappings: MappingItem[]) => void;
  venzaProperties: PropertyGroups[],
  onBack: () => void;
}

export const MappingWizardStep3: React.FC<MappingWizardStep3Props> = ({
  tenantInfo,
  fileName,
  importedData,
  venzaProperties,
  onNext,
  onBack,
}) => {
  const [changes, setChanges] = useState<MappingItem[]>([...importedData, { id: "add-row", venzaProperty: "", encytroOffice: "", status: "", encytroMid: "", isAddRow: true }]);
  const [pciPortalProperties, setPciPortalProperties] = useState<PciPortalProperty[]>([]);
  const [newRow, setNewRow] = useState<MappingItem>()

  const [tempChanges, setTempChanges] = useState<MappingItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isRemovedModalVisible, setIsRemovedModalVisible] = useState(false);
  const [deleteRowConfirmationModalVisible, setDeleteRowConfirmationModalVisible] = useState(false);
  const [selectedDeletedRow, setSelectedDeletedRow] = useState<MappingItem>();
  const [selectedRow, setSelectedRow] = useState<MappingItem>();
  const { t } = useTranslation();

  const visibleChanges = changes.filter(c => (!!c.encytroOffice && !!c.venzaProperty) && c.id !== "add-row" && c.status !== "removed");
  const pendingReview = changes.filter(c => !(!!c.encytroOffice && !!c.venzaProperty) && ((!!c.encytroOffice) || (!!c.venzaProperty)) && c.id !== "add-row" && c.status !== "removed");
  const removedChanges = changes.filter(c => c.status === "removed" && c.id !== "add-row");

  const pendingCount = pendingReview.length;
  const removedCount = removedChanges.length;

  const handleVenzaChange = (record: MappingItem, value: string) => {
    const venzaPropertyName = venzaProperties.find(e => String(e.id) === String(value))?.name;
    if (!!changes.find(c => c.venzaPropertyId === String(value))) {
      setSelectedRow({ ...changes.find(c => c.venzaPropertyId === String(value)), venzaProperty: venzaPropertyName, venzaPropertyId: String(value), venzaPropertyError: "" });
    } else {
      setSelectedRow({ encytroOffice: "", encytroMid: "", venzaProperty: venzaPropertyName, venzaPropertyId: String(value), venzaPropertyError: "" });
    }
    setChanges(prev => prev.map(c => c.venzaPropertyId === record.venzaPropertyId ? { ...c, venzaProperty: venzaPropertyName, venzaPropertyId: String(value), venzaPropertyError: "" } : c));
  };

  const handlePciChange = (record: MappingItem, value: string) => {
    const tempPCI: PciPortalProperty[] = uniqueByMid([
      ...pciPortalProperties,
      {
        name: changes.find(c => c.id === record.id)?.encytroOffice ?? "",
        mapping_status: "unmapped",
        mid: changes.find(c => c.id === record.id)?.encytroMid ?? "",
        property_id: null,
      }]);
    setPciPortalProperties(tempPCI);
    setChanges(prev => prev.map(c => c.venzaPropertyId === record.venzaPropertyId ? { ...c, encytroMid: value, encytroOffice: pciPortalProperties.find(el => el.mid === value)?.name, encytroOfficeError: "" } : c));
  };

  const uniqueByMid = (list: PciPortalProperty[]) => {
    const map = new Map<string, PciPortalProperty>();
    list.forEach(item => {
      if (item.mid) {
        map.set(item.mid, item);
      }
    });
    return Array.from(map.values());
  };

  const handleConfirmMappingItem = () => {
    setChanges([...changes.filter(el => el.id !== selectedDeletedRow?.id)])
    setEditingId(null)
    setDeleteRowConfirmationModalVisible(false)
  }

  const handleAddNewMapping = () => {
    setIsAddingNew(true)
    const newChange: MappingItem = {
      id: `new-${Date.now()}`,
      venzaProperty: "",
      encytroOffice: null,
      status: "matched"
    };
    const addRowRemoved = [...changes, newChange].filter(val => !val.isAddRow)
    setChanges(addRowRemoved);
  };

  const handleContinue = async () => {
    setLoading(true);
    await onNext(changes);
    setLoading(false);
  }

  const handleNewAddRow = (record: MappingItem) => {
    let filtered = changes.filter(item => item.id !== "add-row");
    const uniqueMap = new Map<string, MappingItem>();

    filtered.forEach(item => {
      if (item.id) {
        if (!uniqueMap.has(item.id)) {
          uniqueMap.set(item.id, item);
        }
      }
    });

    filtered = Array.from(uniqueMap.values());

    const mappings = [...filtered.filter(el => el.id !== record.id), record];

    const numericIds = mappings
      .map(el => parseInt(el.id || "0", 10))
      .filter(num => !isNaN(num));
    const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;

    const updatedList: MappingItem[] = mappings.map(el => {
      if (el?.id?.startsWith("new") || el.id === record.id) {
        return { ...el, changeType: "unchanged", id: String(maxId + 1) };
      }
      return el;
    });

    const finalList: MappingItem[] = [
      ...updatedList,
      {
        id: "add-row",
        venzaProperty: "",
        encytroOffice: "",
        status: "",
        isAddRow: true,
      },
    ];

    setIsAddingNew(false);
    setNewRow({});
    setChanges(finalList);
  };


  const handleCancelNewAddRow = () => {
    setChanges(prev =>
      prev.map(item =>
        item?.id?.startsWith("new-")
          ? {
            id: "add-row",
            venzaProperty: "",
            encytroOffice: "",
            status: "",
            isAddRow: true,
          }
          : item
      )
    );
    setIsAddingNew(false);
    setNewRow({});
  }

  const handleNewMapping = () => {
    setChanges([...changes.map(el => el.id === selectedRow?.id ? { ...selectedRow } : { ...el })])
    setEditingId(null);
    setSelectedRow({})
    setTempChanges([]);
  }

  const resetRows = () => {
    setSelectedDeletedRow({} as MappingItem);
    setEditingId(null)
    setDeleteRowConfirmationModalVisible(false)
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        handlePciPropertySearch(value);
      }, DEBOUNCE_TIME),
    []
  );

  const columns: ColumnsType<MappingItem> = [
    {
      title: t("mapping.reviewProperty.propertyMapping.status"),
      dataIndex: "status",
      key: "status",
      render: (_: unknown, record: MappingItem) => {
        if (record.isAddRow && !isAddingNew) {
          return {
            children: (
              <div className="text-center" onClick={handleAddNewMapping}>
                <span data-slot="badge" className="add-new-mapping-button">
                  <Icon type="addNewMapping" />
                  {t("mapping.reviewProperty.propertyMapping.addNewMapping")}
                </span>
              </div>
            ),
            props: { colSpan: 4 },
          };
        }

        if (!!record.encytroOffice && !!record.venzaProperty) {
          return <span data-slot="badge" className="complete-button">
            <Icon type="completed" />{t("mapping.createMapping.step3.matched")}
          </span>;
        }
        return <span data-slot="badge" className="warning-button">
          <Icon type="needsReview" className="review-icon" /> {t("mapping.createMapping.step3.review")}</span>;
      },
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.venzaProperty"),
      dataIndex: "venzaProperty",
      key: "venzaProperty",
      render: (_: unknown, record: MappingItem) => {
        if ((record.isAddRow || record.id?.startsWith("new")) && !!isAddingNew) {
          return (
            <>
              <Select
                placeholder={t("mapping.reviewProperty.propertyMapping.placeholder")}
                // className="w-220 custom-select"
                className="w-220 custom-select"
                dropdownClassName="custom-select-dropdown"
                suffixIcon={<Icon type="selectSuffix" />}
                value={newRow?.venzaProperty || undefined}
                onChange={(val) => setNewRow({ ...newRow, id: val, venzaPropertyId: String(val), venzaProperty: venzaProperties.find((el) => el.id === String(val))?.name })}
              >
              {venzaProperties
                .filter(
                  (el) =>
                    (!changes.filter((e) => e.status !== "removed").find((e) => e.venzaProperty === el.name))
                ).map((v) => (
                  <Option key={v.id} value={Number(v.id)}>
                    {v.name}
                  </Option>
                ))}
              </Select>
              {record.venzaPropertyError && <p className="duplicate-mapping">{record.venzaPropertyError}</p>}
            </>
          );
        }
        const isDuplicate =
          record.venzaProperty &&
          changes.filter(
            (item) =>
              item.venzaProperty === record.venzaProperty &&
              item.id !== record.id && item.status !== "removed"
          ).length > 0;

        return ((!!!record.isAddRow) && (editingId === record.id || (!record.venzaProperty))) ? (
          <>
            <Select
              placeholder={t("mapping.reviewProperty.propertyMapping.placeholder")}
              className="w-220 custom-select"
              dropdownClassName="custom-select-dropdown"
              suffixIcon={<Icon type="selectSuffix" />}
              key={record.id}
              value={!!record.venzaProperty ? record.venzaProperty : undefined}
              onChange={(val) => handleVenzaChange(record, val)}
            >
              {venzaProperties
                .filter(
                  (el) =>
                    (!changes.filter((e) => e.status !== "removed").find((e) => e.venzaProperty === el.name))
                ).map((v) => (
                  <Option key={v.id} value={Number(v.id)}>
                    {v.name}
                  </Option>
                ))}
            </Select>
            {record.venzaPropertyError && <p className="duplicate-mapping">{record.venzaPropertyError}</p>}
          </>
        ) : (
          <>
            {record.venzaProperty}
            {record.venzaPropertyError && <p className="duplicate-mapping">{record.venzaPropertyError}</p>}
            {isDuplicate &&
              <p className="duplicate-mapping">{t("mapping.reviewProperty.propertyMapping.duplicateMapping")}</p>}</>
        );
      },
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.pciPortalProperty"),
      dataIndex: "encytroOffice",
      key: "encytroOffice",
      render: (_: unknown, record: MappingItem) => {
        if ((record.isAddRow || record.id?.startsWith("new")) && !!isAddingNew) {
          return (
            <>
              <Select
                showSearch
                placeholder={t("mapping.reviewProperty.propertyMapping.placeholder")}
                className="w-220 custom-select"
                dropdownClassName="custom-select-dropdown"
                suffixIcon={<Icon type="selectSuffix" />}
                value={newRow?.encytroOffice || undefined}
                onSearch={(value) => {
                  debouncedSearch(value);
                }}
                onChange={(val) => setNewRow({ ...newRow, encytroOffice: pciPortalProperties.find(e => e.mid === val)?.name, encytroMid: val })}
                filterOption={false}
              >
                {pciPortalProperties
                  .filter(
                    (el) =>
                      !changes
                        .filter((e) => e.action !== "remove")
                        .find((e) => e.encytroOffice === el.name)
                  )
                  .map((p, index) => (
                    <Option key={p.mid + "" + index} value={p.mid}>
                      {p.name}
                    </Option>
                  ))}
              </Select>
              {record.encytroOfficeError && <p className="duplicate-mapping">{record.encytroOfficeError}</p>}
            </>
          );
        }

        const isDuplicate =
          record.encytroOffice &&
          changes.filter(
            (item) =>
              item.encytroOffice === record.encytroOffice &&
              item.id !== record.id && record.status !== "removed"
          ).length > 0;

        return ((!!!record.isAddRow) && (editingId === record.id || (!record.encytroOffice))) ? (
          <>
            <Select
              showSearch
              placeholder={t("mapping.reviewProperty.propertyMapping.placeholder")}
              className="w-220 custom-select"
              dropdownClassName="custom-select-dropdown"
              suffixIcon={<Icon type="selectSuffix" />}
              value={!!record.encytroOffice ? record.encytroOffice : undefined}
              onSearch={(value) => {
                debouncedSearch(value);
              }}
              onChange={(val) => handlePciChange(record, val)}
              filterOption={false}
            >
              {pciPortalProperties
                .filter(
                  (el) =>
                    !changes
                      .filter((e) => e.action !== "remove")
                      .find((e) => e.encytroOffice === el.name)
                )
                .map((p) => (
                  <Option key={p.mid} value={p.mid}>
                    {p.name}
                  </Option>
                ))}
            </Select>
            {record.encytroOfficeError && <p className="duplicate-mapping">{record.encytroOfficeError}</p>}
          </>
        ) : (
          <>
            {record.encytroOffice}
            {record.encytroOfficeError && <p className="duplicate-mapping">{record.encytroOfficeError}</p>}
            {isDuplicate &&
              <p className="duplicate-mapping">{t("mapping.reviewProperty.propertyMapping.duplicateMapping")}</p>}</>
        );
      },
    },
    {
      title: t("mapping.table.actions"),
      key: "actions",
      render: (_: unknown, record: MappingItem) => {
        if (record.isAddRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        if (record.id?.startsWith("new")) {
          const isAddEnabled = !!newRow?.encytroOffice && !!newRow?.venzaProperty;
          return (
            <Space>
              <Button
                type="default"
                className="create-mapping-button"
                disabled={!isAddEnabled}
                onClick={() =>
                  handleNewAddRow({
                    ...record,
                    encytroMid: newRow?.encytroMid ?? "",
                    venzaPropertyId: newRow?.venzaPropertyId ?? "",
                    encytroOffice: newRow?.encytroOffice ?? "",
                    venzaProperty: newRow?.venzaProperty ?? "",
                  })
                }
                style={{
                  marginRight: "0px",
                  opacity: !isAddEnabled ? 0.5 : 1,
                  backgroundColor: !isAddEnabled ? "rgb(3, 2, 19)" : "white",
                  color: !isAddEnabled ? "white" : "rgb(3, 2, 19)",
                }}
              >
                {t("mapping.reviewProperty.propertyMapping.add")}
              </Button>

              <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => handleCancelNewAddRow()}
              >
                {t("mapping.reviewProperty.propertyMapping.cancel")}
              </Button>
            </Space>
          );
        }

        if (record.id === editingId) {
          return (
            <Space>
              <Button
                type="default"
                className="create-mapping-button bg-primary color-white"
                onClick={() => handleNewMapping()}
              >
                {t("mapping.reviewProperty.propertyMapping.save")}
              </Button>

              <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => {
                  setEditingId(null);
                  setChanges([...tempChanges]);
                }}
              >
                {t("mapping.reviewProperty.propertyMapping.cancel")}
              </Button>
            </Space>
          );
        }

        if (record.venzaProperty || record.encytroOffice) {
          if (record.venzaProperty && record.encytroOffice) {
            return (
              <Space>
                <Button
                  type="default"
                  className="create-mapping-button"
                  onClick={() => {
                    setTempChanges(changes);
                    setEditingId(record.id ?? "");
                  }}
                  icon={
                    <Icon type="mappingEdit" />
                  }
                >
                  {t("mapping.reviewProperty.propertyMapping.edit")}
                </Button>

                <Button
                  type="default"
                  className="create-mapping-button mr-0"
                  onClick={() => {
                    setDeleteRowConfirmationModalVisible(true);
                    setSelectedDeletedRow(record);
                  }}
                  icon={
                    <Icon type="mappingTrash" />
                  }
                >
                  {t("mapping.reviewProperty.propertyMapping.delete")}
                </Button>
              </Space>
            );
          }

          return (
            <Space>
              <Button
                type="default"
                className="create-mapping-button"
                onClick={() => {
                  setTempChanges(changes);
                  setEditingId(record.id ?? "");
                }}
                icon={
                  <Icon type="mappingEdit" />
                }
              >
                {t("mapping.reviewProperty.propertyMapping.edit")}
              </Button>

              <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => {
                  setChanges(
                    changes.map((c) =>
                      c.id === record.id ? { ...c, status: "removed" } : c
                    )
                  );
                }}
                icon={
                  <Icon type="mappingIgnore" />
                }
              >
                {t("mapping.reviewProperty.propertyMapping.remove")}
              </Button>
            </Space>
          );
        }

        return <></>;
      },
    }

  ];

  const removedColumns = [
    {
      title: t("mapping.reviewProperty.propertyMapping.status"),
      key: "status",
      render: () => (
        <Tag
          icon={<Icon type="mappingIgnore" />}
          className="mapping-ignore-button"
        >
          {t("mapping.reviewProperty.propertyMapping.removed")}
        </Tag>
      ),
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.venzaProperty"),
      dataIndex: "venzaProperty",
      key: "venzaProperty",
      render: (text: string) =>
        text ? (
          text
        ) : (
          <span className="italic-font">{t("mapping.reviewProperty.propertyMapping.notMapped")}</span>
        ),
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.pciPortalProperty"),
      dataIndex: "encytroOffice",
      key: "encytroOffice",
    },
    {
      title: t("mapping.table.actions"),
      key: "actions",
      render: (_: unknown, record: MappingItem) => (
        <Space>
          <Button
            type="default"
            className="create-mapping-button"
            onClick={() => {
              setChanges([...changes.map(el => el.id === record.id ? { ...el, status: "needs review" as const } : { ...el })])

            }}
          >
            {t("mapping.reviewProperty.propertyMapping.restore")}
          </Button>
        </Space>
      ),
    },
  ];

  const handlePciPropertySearch = async (search: string) => {
    try {
      if (!!search && search.trim().length < 3) {
        return;
      }
      const properties = await getExternalMerchants(
        Number(tenantInfo.id),
        Number(tenantInfo.providerId),
        search ?? ""
      );
      const tempPCI: PciPortalProperty[] = uniqueByMid([
        ...properties,
        ...importedData.map(el => ({
          mapping_status: "unmapped",
          mid: el.encytroMid ?? "",
          name: el.encytroOffice ?? "",
          property_id: null,
        }))
      ]);
      setPciPortalProperties(tempPCI);
    } catch (error) {
      message.error(t("mapping.errors.loadProperties"));
    }
  };

  useEffect(() => {
    handlePciPropertySearch("");
  }, []);

  useEffect(() => {
    const syncMID = async () => {
      setLoading(true);

      const mids = importedData
        .map(i => i.encytroMid)
        .filter(Boolean);

      if (mids.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const result = await getExternalMerchants(
          Number(tenantInfo.id),
          Number(tenantInfo.providerId),
          "",
          mids.join(",")
        );

        const resultMap: Record<string, any> = {};
        result?.forEach(r => {
          resultMap[r.mid] = r;
        });

        const updated = importedData.filter(item => !!item.encytroMid).map(item => {
          const key = item.encytroMid ?? "";
          const found = resultMap[key];

          if (!found) {
            return {
              ...item,
              encytroMid: "",
              encytroOffice: "",
              encytroOfficeError: (!!item.encytroOffice && !!item.encytroMid)
                ? `${item.encytroOffice} ${t("mapping.createMapping.step3.propertyNoFound")}`
                : ((!item.encytroOffice && !!item.encytroMid) ? `${t("mapping.createMapping.step3.propertyNoFound")}` : ""),
            };
          }
          return {
            ...item,
            encytroMid: found.mid || item.encytroMid,
            encytroOffice: found.name || item.encytroOffice,
          };
        });

        setChanges([
          ...updated,
          ...importedData.filter(item => !item.encytroMid && !!item.venzaProperty),
          { id: "add-row", venzaProperty: "", encytroOffice: "", status: "", encytroMid: "", isAddRow: true }
        ]);

      } catch (err) {
        console.error(err);
      }

      setLoading(false);

      setPciPortalProperties([]);
      setNewRow(undefined);
      setTempChanges([]);
      setEditingId(null);
      setIsAddingNew(false);
      setIsRemovedModalVisible(false);
      setDeleteRowConfirmationModalVisible(false);
      setSelectedDeletedRow(undefined);
      setSelectedRow(undefined);
    };

    syncMID();
    handlePciPropertySearch("");
  }, [importedData]);

  const venzaValues = changes.filter(item => item.status !== "removed").map(item => item.venzaProperty).filter(Boolean);
  const pciValues = changes.filter(item => item.status !== "removed").map(item => item.encytroOffice).filter(Boolean);

  const isDuplicateVenza = venzaValues.some(
    (val, idx) => venzaValues.indexOf(val) !== idx
  );

  const isDuplicatePCI = pciValues.some(
    (val, idx) => pciValues.indexOf(val) !== idx
  );

  const hasUnresolvedFields = changes.some(el =>
    el.status !== "removed" &&
    el.id !== "add-row" &&
    (!el.venzaProperty ||
      !el.venzaPropertyId ||
      !el.encytroOffice ||
      !el.encytroMid)
  );

  const disabledPropertyButton = hasUnresolvedFields ||
    isDuplicateVenza ||
    isDuplicatePCI || editingId || isAddingNew;
  let buttonLabel = "mapping.unmapping.continue";
  if (isDuplicateVenza || isDuplicatePCI) {
    buttonLabel = "mapping.createMapping.step3.resolveDuplicate";
  } else if (hasUnresolvedFields) {
    buttonLabel = "mapping.createMapping.step3.resolveAll";
  }

  return (
    <Card>
      <div className="mbx-16">
        <Title level={3} className="headerTitle">{t("mapping.createMapping.step3.title")}</Title>
        <p className="headerSubtitle">{t("mapping.createMapping.step3.title")}</p>
      </div>

      <div className="tenantInfo">
        <div>
          <strong>{t("mapping.reviewProperty.propertyMapping.client")}</strong>
          <p>{tenantInfo.name}</p>
        </div>
        <div>
          <strong>{t("mapping.createMapping.step3.file")}</strong>
          <p>{fileName}</p>
        </div>
      </div>

      <div className="grid-button">
        <Card className="statusCard newCard" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <Icon type="matches" />
            <p>{t("mapping.createMapping.step3.matches")}</p>
          </div>
          <p className="cardValue">{visibleChanges.length}</p>
        </Card>

        <Card className="statusCard pendingCard" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <ExclamationCircleOutlined className="cardIcon" />
            <p>{t("mapping.reviewProperty.propertyMapping.needsReview")}</p>
          </div>
          <p className="cardValue">{pendingCount}</p>
        </Card>

        <Card className="statusCard ignoredCard" hoverable onClick={() => setIsRemovedModalVisible(true)} bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <MinusCircleOutlined className="cardIcon" />
            <p>{t("mapping.reviewProperty.propertyMapping.removed")}</p>
          </div>
          <p className="cardValue">{removedCount}</p>
        </Card>
      </div>

      <Tabs defaultActiveKey="all" className="custom-pill-tabs">
        <TabPane tab={`${t("mapping.reviewProperty.propertyMapping.all")} (${visibleChanges.length + pendingReview.length})`} key="all">
          <Spin spinning={loading}>
            <Table rowKey="id" dataSource={changes.filter(el => el.status !== 'removed')} columns={columns} pagination={false} />
          </Spin>
        </TabPane>

        <TabPane tab={`${t("mapping.createMapping.step3.matched")} (${visibleChanges.length})`} key="new">
          <Spin spinning={loading}>
            <Table rowKey="id" dataSource={visibleChanges} columns={columns} pagination={false} />
          </Spin>
        </TabPane>

        <TabPane tab={`${t("mapping.reviewProperty.propertyMapping.needsReview")} (${pendingReview.length})`} key="mapped">
          <Spin spinning={loading}>
            <Table rowKey="id" dataSource={pendingReview} columns={columns} pagination={false} />
          </Spin>
        </TabPane>
      </Tabs>

      <div className="footerButtons">
        <Button className="backButton" onClick={onBack}>{t("mapping.reviewProperty.propertyMapping.back")}</Button>
        <Button className={`continueButton ${(disabledPropertyButton) ? "opacity-50" : ""}`} type="primary" onClick={handleContinue} disabled={(!!disabledPropertyButton)}>
          {t(buttonLabel)}
        </Button>
      </div>

      <Modal visible={isRemovedModalVisible} className="ignoredMappings" onCancel={() => setIsRemovedModalVisible(false)} footer={null} closable={false} >
        <div className="modalHeader">
          <Title level={3} className="modalTitle">{t("mapping.reviewProperty.propertyMapping.removedChangesTitle")}</Title>
          <p className="modalSubtitle">{t("mapping.reviewProperty.propertyMapping.removedChangesSubtitle")}</p>
        </div>
        <Table
          rowKey="id"
          dataSource={changes
            .filter(dt => dt.status === "removed")
            .map(dt => ({
              ...dt,
              encytroOffice: dt.encytroOffice ?? "",
              venzaProperty: dt.venzaProperty ?? "",
              encytroMid: dt.encytroMid ?? "",
            }))}
          columns={removedColumns}
          pagination={false}
        />
      </Modal>

      <Modal className="confirmMapping" title={t("mapping.createMapping.step3.areYouSure")} visible={deleteRowConfirmationModalVisible} onCancel={resetRows} footer={null} closable={false} centered>
        <form className="confirmMappingForm" onSubmit={(e) => { e.preventDefault(); handleConfirmMappingItem(); }}>
          <p className="confirmMappingDescription">{t("mapping.createMapping.step3.areYouSureDesc")}</p>

          <div className="confirmButtonsWrapper d-flex justify-end mtx-16 gapx-8">
            <Button className="backButton" onClick={resetRows}>{t("mapping.reviewProperty.propertyMapping.cancel")}</Button>
            <Button htmlType="submit" className="confirmButton" type="primary">{t("mapping.reviewProperty.propertyMapping.remove")}</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );

};
